import { useContext, useEffect, useState } from 'react';
import Mousetrap from 'assets/js/better-mousetrap';

import { DocumentValuesContext } from 'contexts';
import { getLocalSettingsProxy } from 'utilities';

/**
 * This hook can attach a keyboard shortcut combination as a listener in a component.
 *
 * Key names follow the conventions used by Mousetrap,
 * which is the same library used in the V6 eReader by the ember-keyboard-shortcuts addon.
 *
 * https://github.com/ccampbell/mousetrap
 *
 * Example usage with default options:
 *     useKeyboardShortcut('ctrl+shift+a', myCallback, {
 *         target: null, // an element or an array of elements, leave unset for global
 *         event: 'keyup',
 *     });
 *
 * To call preventDefault and stopPropagation, make the callback return false.
 *
 * @param {String} keys - key combination, as a string or array of strings, to trigger the keyboard shortcut callback
 * @param {Function} callback - function to call when the keyboard shortcut is triggered
 * @param {Object} options - allows specifying targets to bind to and which event to listen to
 */
export default function useKeyboardShortcut(
  keys,
  callback = () => {},
  options = {},
) {
  const { frameDocument } = useContext(DocumentValuesContext);

  const [localSettings] = useState(getLocalSettingsProxy);

  /**
   * Attach and detach listeners.
   */
  useEffect(() => {
    const defaultTargets = [
      document.body,
      ...(frameDocument ? [frameDocument] : []),
    ];
    const targets = options.target
      ? Array.isArray(options.target)
        ? options.target
        : [options.target]
      : defaultTargets;
    const event = options.event || 'keyup';

    let expired = false;

    const boundFunction = () => {
      // In case it changes, check whether keyboard shortcuts are enabled whenever the shortcut is triggered.
      if (!expired && localSettings.useKeyboardShortcuts) {
        return callback();
      }

      // Do not call preventDefault and stopPropagation if the callback does not run.
      return true;
    };

    const mousetraps = [];

    for (let target of targets) {
      const mousetrap = new Mousetrap(target);
      mousetraps.push(mousetrap);
      mousetrap.bind(keys, boundFunction, event);
    }

    return () => {
      for (let mousetrap of mousetraps) {
        mousetrap.removeListeners();
      }
    };
  }, [callback, frameDocument, keys, localSettings, options]);
}
